<template>
  <transition name="fade">
    <div v-if="visible" class="video-edit-overlay">
      <!-- 加载 -->
      <!-- <Loading :loading="isLoading"></Loading> -->
      <!-- 顶部工具栏 -->
      <div class="toolbar">
        <div class="toolbar-content">
          <div class="toolbar-left">
            <!-- 集数选择：切换当前剧集（加载对应视频与字幕） -->
            <a-select
              v-model:value="selectedEpisodeNumber"
              class="episode-select"
              placeholder="选择集数"
              @change="handleEpisodeChange"
            >
              <a-select-option v-for="ep in episodes" :key="ep.episodeNumber" :value="ep.episodeNumber">
                第{{ ep.episodeNumber }}集
              </a-select-option>
            </a-select>
            <a-button 
              type="default" 
              class="episode-next-btn"
              :disabled="isLastEpisode"
              @click="handleNextEpisode"
            >
              下一集
            </a-button>
          </div>
          <div class="toolbar-spacer"></div>
          <!-- 替换按钮 -->
          <a-button style="margin-right: 8px;" @click="openReplaceModal">替换字幕</a-button>
          <!-- 重新配音按钮 -->
          <a-button type="primary" style="margin-right: 8px;" @click="handleReDubbing">保存并重新配音</a-button>
          <a-button @click="onClose">关闭</a-button>
        </div>
      </div>
      
      <!-- 底部操作视图 -->
      <div class="content-area">
        <!-- 左侧视频区域 -->
        <div class="video-section">
          <!-- 视频源选择（悬浮于视频左上角） -->
          <div class="video-source-toolbar">
            <a-input-group compact style="white-space: nowrap; opacity: 0.7;">
              <!-- 视频类型选择 -->
              <a-dropdown placement="bottomLeft" :trigger="['hover']">
                <a-button class="source-left-btn" :type="!isDubbingTemp ? 'primary' : 'default'" @click="handleVideoTypeToggle">
                  {{ currentVideoTypeLabel }}
                  <DownOutlined style="margin-left: 4px;" />
                </a-button>
                <template #overlay>
                  <a-menu @click="handleVideoTypeChange" :selectedKeys="[String(selectedVideoType)]">
                    <a-menu-item v-for="opt in YoutubeVideoTypes" :key="opt.value">
                      {{ opt.text }}
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
              <!-- 配音视频选择 -->
              <a-button
                :type="isDubbingTemp ? 'primary' : 'default'"
                class="source-right-btn"
                @click="toggleDubbing"
              >
                配音视频
              </a-button>
            </a-input-group>
          </div>
          <!-- 视频播放器 -->
          <video
            ref="videoRef"
            class="video-player"
            autoplay
            :src="videoUrl"
            @timeupdate="handleTimeUpdate"
            @loadedmetadata="handleVideoLoaded"
            @durationchange="handleDurationChange"
            @play="handlePlay"
            @pause="handlePause"
            @click="togglePlayPause"
          ></video>
          
          <!-- 翻译字幕覆盖层 -->
          <div 
            v-if="displaySubtitle && showSubtitle"
            class="subtitle-overlay subtitle-overlay-translated"
            :style="subtitleStyle"
          >
            {{ displaySubtitle.text }}
          </div>
          
          <!-- 原版字幕覆盖层 -->
          <div 
            v-if="displayOriginalSubtitle && showOriginalSubtitle"
            class="subtitle-overlay subtitle-overlay-original"
            :style="originalSubtitleStyle"
          >
            {{ displayOriginalSubtitle.text }}
          </div>
          
          <!-- 底部视频工具栏 -->
          <div class="video-toolbar">
            <!-- 进度条 -->
            <div class="video-progress">
              <!-- 时间显示 -->
              <div class="time-display">
                <span class="current-time">{{ formatTime(currentTime) }}</span>
                <span class="duration-time">{{ formatTime(duration) }}</span>
              </div>
              <!-- 进度条 -->
              <div 
                class="progress-bar-wrapper"
                @click="handleProgressClick"
                @mousedown="handleProgressMouseDown"
              >
                <div class="progress-bar-bg">
                  <div 
                    class="progress-bar-fill"
                    :style="{ width: progressPercent + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            
            <!-- 功能按钮条 -->
            <div class="video-controls">
              <!-- 左侧：隐藏占位（保持播放按钮对称居中） -->
              <div class="control-placeholder"></div>
              
              <!-- 中间：播放控制按钮 -->
              <div class="control-center">
                <a-tooltip title="后退0.5秒">
                  <a-button type="text" class="control-btn icon-btn" @click="seekBackward">
                    <template #icon><BackwardOutlined /></template>
                  </a-button>
                </a-tooltip>
                <a-button type="text" class="control-btn icon-btn" @click="togglePlayPause">
                  <template #icon>
                    <CaretRightOutlined v-if="!isPlaying" />
                    <PauseOutlined v-else />
                  </template>
                </a-button>
                <a-tooltip title="前进0.5秒">
                  <a-button type="text" class="control-btn icon-btn" @click="seekForward">
                    <template #icon><ForwardOutlined /></template>
                  </a-button>
                </a-tooltip>
              </div>
              
              <!-- 右侧：播放速度 -->
              <a-dropdown placement="top">
                <a-button type="text" class="control-btn playback-rate-btn">
                  {{ playbackRate }}x
                </a-button>
                <template #overlay>
                  <a-menu @click="handlePlaybackRateChange">
                    <a-menu-item v-for="rate in playbackRates" :key="rate" :value="rate">
                      {{ rate }}x
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </div>
          </div>
        </div>
        
        <!-- 字幕容器 -->
        <div class="subtitle-container" ref="subtitleContainerRef">
          <!-- 翻译字幕区域（含原字幕对照列） -->
          <div 
            class="translated-subtitle-section"
            ref="translatedSubtitleRef"
          >
            <div class="subtitle-list">
              <!-- 表头 -->
              <div class="subtitle-header">
                <!-- 表头：时间轴列 -->
                <div class="subtitle-header-cell header-col-time">时间轴</div>
                <!-- 表头：原字幕列 -->
                <div class="subtitle-header-cell header-col-original">
                  <div class="header-flex">
                    <span>原版字幕</span>
                    <div class="header-flex-spacer"></div>
                    <a-tooltip :title="showOriginalSubtitle ? '隐藏原版字幕' : '显示原版字幕'">
                      <a-button type="text" size="small" class="header-subtitle-toggle" :class="{ 'subtitle-off': !showOriginalSubtitle }" @click.stop="toggleOriginalSubtitleVisibility">
                        <template #icon>
                          <EyeOutlined v-if="showOriginalSubtitle" />
                          <EyeInvisibleOutlined v-else />
                        </template>
                      </a-button>
                    </a-tooltip>
                  </div>
                </div>
                <!-- 表头：翻译字幕列 -->
                <div class="subtitle-header-cell header-col-translated">
                  <div class="header-flex">
                    <span>翻译字幕</span>
                    <div class="header-flex-spacer"></div>
                    <a-tooltip :title="showSubtitle ? '隐藏翻译字幕' : '显示翻译字幕'">
                      <a-button type="text" size="small" class="header-subtitle-toggle" :class="{ 'subtitle-off': !showSubtitle }" @click.stop="toggleSubtitle">
                        <template #icon>
                          <EyeOutlined v-if="showSubtitle" />
                          <EyeInvisibleOutlined v-else />
                        </template>
                      </a-button>
                    </a-tooltip>
                  </div>
                </div>
                <!-- 表头：语速列（说明提示） -->
                <div class="subtitle-header-cell header-col-speed">
                  语速
                  <a-tooltip>
                    <template #title>语速（字/秒）= 字数 / (结束时间-开始时间)；用于调节语速，字数为翻译字幕字符串长度</template>
                    <InfoCircleOutlined />
                  </a-tooltip>
                </div>
              </div>
              <!-- 字幕项分组（工具栏 + 字幕内容） -->
              <div 
                v-for="(item, index) in translatedSubtitles" 
                :key="index" 
                :id="`translated-subtitle-${index}`"
                class="subtitle-item-group"
                :class="{ 'subtitle-item-group-active': item === currentSubtitle }"
                @click="handleSubtitleClick(item, index)"
              >
                <!-- 工具栏行 -->
                <div class="subtitle-item-toolbar">
                  <div class="toolbar-cell toolbar-col-time">
                    <span class="subtitle-index">#{{ index + 1 }}</span>
                  </div>
                  <div class="toolbar-cell toolbar-col-original">
                    <a-select
                      :value="item.speakerId || undefined"
                      placeholder="选择说话人"
                      size="small"
                      class="speaker-select"
                      :disabled="speakers.length === 0"
                      @change="(val) => handleSpeakerChange(val, item, index)"
                      @click.stop
                      :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                    >
                      <a-select-option v-for="spk in speakers" :key="spk.Id" :value="spk.Id">
                        {{ spk.Name }}
                      </a-select-option>
                    </a-select>
                    <a class="translate-btn" :class="{ 'translate-btn-loading': item._translating }" @click.stop="handleTranslateSubtitle(index)">
                      {{ item._translating ? '翻译中...' : '翻译' }}
                    </a>
                  </div>
                  <div class="toolbar-cell toolbar-col-translated"></div>
                  <div class="toolbar-cell toolbar-col-speed"></div>
                </div>
                <!-- 字幕内容行 -->
                <div 
                  class="subtitle-item subtitle-item-translated"
                >
                  <div class="subtitle-cell subtitle-time">
                    <input 
                      type="text" 
                      class="time-input time-start-input"
                      :class="{ 'input-error': !isTimeFormatValid(item.startTime) }"
                      v-model="item.startTime"
                      @input="handleTimeInput($event, item, 'startTime', index)"
                      @blur="handleTimeBlur($event, item, 'startTime', index)"
                      @click.stop
                    />
                    <input 
                      type="text" 
                      class="time-input time-end-input"
                      :class="{ 'input-error': !isTimeFormatValid(item.endTime) || item.endTimeRed }"
                      v-model="item.endTime"
                      @input="handleTimeInput($event, item, 'endTime', index)"
                      @blur="handleTimeBlur($event, item, 'endTime', index)"
                      @click.stop
                    />
                  </div>
                  <div class="subtitle-cell subtitle-original-text">
                    <textarea 
                      class="subtitle-textarea subtitle-textarea-original"
                      :value="originalSubtitles[index]?.text || ''"
                      @input="handleOriginalTextInput($event, index)"
                      @keydown.enter.prevent
                      @click.stop
                    ></textarea>
                  </div>
                  <div class="subtitle-cell subtitle-text">
                    <textarea 
                      class="subtitle-textarea"
                      v-model="item.text"
                      @input="handleTextInput($event, item, index)"
                      @blur="handleTextBlur($event, item, index)"
                      @keydown="handleTextKeydown($event, item, index)"
                      @click.stop
                    ></textarea>
                  </div>
                  <div class="subtitle-cell subtitle-speed" :class="{ 'text-red': item.speedRed }">
                    {{ calculateSpeed(item.startSeconds, item.endSeconds, item.text) }}
                  </div>
                  <!-- 合并/添加按钮（行底部居中，hover显示） -->
                  <div class="subtitle-divider-buttons" v-if="shouldShowDividerButtons(item, index)" @click.stop>
                    <a-tooltip title="向上合并字幕">
                      <a-button 
                        type="text" 
                        size="small" 
                        class="divider-btn"
                        @click="handleMergeSubtitle(index)"
                        v-if="index > 0"
                      >
                        <template #icon><VerticalAlignTopOutlined /></template>
                      </a-button>
                    </a-tooltip>
                    <a-tooltip title="向下添加字幕">
                      <a-button 
                        type="text" 
                        size="small" 
                        class="divider-btn"
                        @click="handleAddSubtitle(index)"
                      >
                        <template #icon><PlusCircleOutlined /></template>
                      </a-button>
                    </a-tooltip>
                  </div>
                  <!-- 删除按钮（行尾部，hover显示） -->
                  <div class="subtitle-delete-btn" @click.stop>
                    <a-tooltip title="删除">
                      <a-button 
                        type="text" 
                        size="small" 
                        class="divider-btn delete-btn"
                        @click="handleDeleteSubtitle(index)"
                      >
                        <template #icon><DeleteOutlined /></template>
                      </a-button>
                    </a-tooltip>
                  </div>
                </div>
              </div>
              <!-- 最后一行占位（如果最后一行显示加号） -->
              <div 
                v-if="translatedSubtitles.length > 0 && shouldShowDividerButtons(translatedSubtitles[translatedSubtitles.length - 1], translatedSubtitles.length - 1)"
                class="subtitle-placeholder"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
  <!-- 挂载替换弹窗（保持同一模板内） -->
  <VideoEditReplace ref="replaceModalRef" @confirm="handleReplaceConfirm" />
</template>

<script setup>
import { ref, nextTick, watch, computed } from 'vue'
import { message, Modal } from 'ant-design-vue'
import Loading from '@/components/Loading'

// 引入图标
import { 
  InfoCircleOutlined, 
  VerticalAlignTopOutlined, 
  PlusCircleOutlined,
  DeleteOutlined,
  BackwardOutlined,
  CaretRightOutlined,
  PauseOutlined,
  ForwardOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  DownOutlined
} from '@ant-design/icons-vue'
// 引入字幕模拟数据
// 引入字幕与剧集 mock 数据接口：
// - getSeriesWithSubtitles：统一返回剧集列表与默认集（附带字幕）
// - getOriginalSubtitles / getTranslatedSubtitles：作为回退使用
import { getOriginalSubtitles, getTranslatedSubtitles, getSeriesWithSubtitles } from '@/utils/subtitleMock'
import { YoutubeVideoTypes, YoutubeVideoTypesUpgrade, YoutubeVideoTypeObject } from '@/utils/constantList'
import VideoEditReplace from './VideoEditReplace.vue'
// 引入 API
import { taskEqDetail, taskEqTypeUrl, taskUpdateSubtitles, taskCreateSubtitle, taskRemoveSubtitle, taskTranslateSubtitleText } from '@/api/admin'

// emit
const emit = defineEmits(['success'])
// 显示
let visible = ref(false)
// 剧集对象
let dramaDetail = ref({})
// 集对象
let eqDetail = ref({})
// 当前集对象
let detail = ref({})
// 加载
let isLoading = ref(false)
// 翻译字幕是否有编辑操作
let hasEditedSubtitle = ref(false)
// 视频总时长（秒），默认0
const videoDuration = ref(0)

// 测试数据 - 原字幕列表
const originalSubtitles = ref([])
// 测试数据 - 翻译字幕列表
const translatedSubtitles = ref([])
// 剧集选择相关状态
const episodes = ref([]) // 全部剧集（1-30 集）
const selectedEpisodeNumber = ref(null) // 当前选中“第几集”（用于 a-select 绑定）
const currentEpisode = ref(null) // 当前集完整对象（便于直接访问 videoUrls 与字幕）
const seriesInfo = ref(null) // 剧级别信息（含默认集ID）
// 替换弹窗
const replaceModalRef = ref(null)

// DOM 引用
const subtitleContainerRef = ref(null)
const translatedSubtitleRef = ref(null)

// 视频相关
const videoRef = ref(null)
const videoUrl = ref('') // 视频URL，后续从record获取
const isPlaying = ref(false)
const playbackRate = ref(1)
const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
const showSubtitle = ref(true)
const showOriginalSubtitle = ref(true)
const currentTime = ref(0)
const duration = ref(0)
// 视频源切换：左侧下拉记录"非配音的类型"；配音单独用按钮控制，不影响下拉
const selectedVideoType = ref(YoutubeVideoTypeObject.full)
const isDubbingTemp = ref(false)
// 视频URL缓存：存储不同类型视频的URL
const videoUrlCache = ref({
  [YoutubeVideoTypeObject.full]: '', // 完整版本
  [YoutubeVideoTypeObject.no_bgm]: '', // 无BGM版本
  [YoutubeVideoTypeObject.no_subtitle]: '', // 无字幕版本
  dubbing: '' // 配音版本
})

// 字幕显示相关
const currentSubtitle = ref(null) // 当前显示的翻译字幕（用于列表高亮，保持不消失）
const displaySubtitle = ref(null) // 视频上应该显示的翻译字幕（按时间判断）
const subtitleStyle = ref({}) // 翻译字幕样式
const currentOriginalSubtitle = ref(null) // 当前显示的原版字幕（用于列表高亮，保持不消失）
const displayOriginalSubtitle = ref(null) // 视频上应该显示的原版字幕（按时间判断）
const originalSubtitleStyle = ref({}) // 原版字幕样式
let animationFrameId = null
let resizeObserver = null
// 强制显示的字幕（点击选中时设置）
const forcedSubtitle = ref(null)
const forcedOriginalSubtitle = ref(null)

// 计算进度百分比
const progressPercent = computed(() => {
  if (!duration.value || duration.value === 0) {
    return 0
  }
  return (currentTime.value / duration.value) * 100
})

// a-select 选项（集数下拉）
const episodeOptions = computed(() => {
  if (!episodes.value || episodes.value.length === 0) {
    return []
  }
  return episodes.value.map((episode) => ({
    label: `第${episode.episodeNumber}集`,
    value: episode.episodeNumber
  }))
})

// 说话人列表（从集详情获取）
const speakers = computed(() => {
  return detail.value?.speakers || []
})

// 当前视频类型标签
const currentVideoTypeLabel = computed(() => {
  const found = YoutubeVideoTypesUpgrade.find(o => o.value === selectedVideoType.value)
  return found ? found.text : '完整版本'
})

// 当前集索引与“是否最后一集”
const currentEpisodeIndex = computed(() => {
  if (!episodes.value || episodes.value.length === 0) {
    return -1
  }
  const num = selectedEpisodeNumber.value || currentEpisode.value?.episodeNumber
  return episodes.value.findIndex((e) => e.episodeNumber === num)
})

const isLastEpisode = computed(() => {
  if (!episodes.value || episodes.value.length === 0) return true
  const idx = currentEpisodeIndex.value
  return idx === -1 || idx >= episodes.value.length - 1
})

// 跳到下一集
function handleNextEpisode() {
  if (!episodes.value || episodes.value.length === 0) {
    return
  }
  const idx = currentEpisodeIndex.value
  if (idx === -1 || idx >= episodes.value.length - 1) {
    return
  }
  const nextEp = episodes.value[idx + 1]
  if (nextEp && nextEp.episodeNumber) {
    // 调用接口获取集详情，而不是直接加载本地数据
    getEqDetail(nextEp.episodeNumber)
  }
}

// 仅清空“字幕展示与选中状态”（不影响字幕数据）
function clearSubtitleDisplaySelection() {
  currentSubtitle.value = null
  displaySubtitle.value = null
  currentOriginalSubtitle.value = null
  displayOriginalSubtitle.value = null
  forcedSubtitle.value = null
  forcedOriginalSubtitle.value = null
}

// 根据当前选择的类型/配音状态，应用视频源
function applyCurrentVideoSource() {
  if (!detail.value || !eqDetail.value.task_id) {
    videoUrl.value = ''
    // 重置进度与时长
    currentTime.value = 0
    duration.value = 0
    videoDuration.value = 0
    return
  }

  // 切换视频源时，清空字幕展示/选中状态
  clearSubtitleDisplaySelection()

  // 切换视频源
  if (isDubbingTemp.value) {
    // 配音视频：使用 output_video_url
    videoUrl.value = videoUrlCache.value.dubbing || ''
  } else if (selectedVideoType.value === YoutubeVideoTypeObject.full) {
    // 完整版本：使用 video_url
    videoUrl.value = videoUrlCache.value[YoutubeVideoTypeObject.full] || ''
  } else {
    // 无BGM版本或无字幕版本：从缓存获取，如果缓存没有则调用接口
    const cachedUrl = videoUrlCache.value[selectedVideoType.value]
    if (cachedUrl) {
      videoUrl.value = cachedUrl
    } else {
      // 调用接口获取视频地址
      getVideoTypeUrl(selectedVideoType.value)
      return // 接口调用中，等待回调更新
    }
  }

  // 强制刷新视频源，保证切换后立即生效
  nextTick(() => {
    if (videoRef.value) {
      try { videoRef.value.pause() } catch (e) {}
      // 切换源前重置播放时间到0，进度归零
      try { videoRef.value.currentTime = 0 } catch (e) {}
      // 重置响应式进度与时长，等待 metadata/duration 事件恢复
      isPlaying.value = false
      currentTime.value = 0
      duration.value = 0
      videoDuration.value = 0
      try { if (typeof videoRef.value.load === 'function') videoRef.value.load() } catch (e) {}
    }
  })
}

// 获取指定类型的视频URL
function getVideoTypeUrl(type) {
  if (!eqDetail.value.task_id || !selectedEpisodeNumber.value) {
    console.warn('缺少必要参数，无法获取视频地址')
    return
  }
  
  const params = {
    task_id: eqDetail.value.task_id,
    eq_number: selectedEpisodeNumber.value,
    type: type
  }
  
  taskEqTypeUrl(params).then(res => {
    const { code, data } = res
    if (code === 0 && data && data.video_url) {
      // 缓存视频URL
      videoUrlCache.value[type] = data.video_url
      // 如果当前选中的类型匹配，立即应用
      if (selectedVideoType.value === type && !isDubbingTemp.value) {
        videoUrl.value = data.video_url
        // 强制刷新视频源
        nextTick(() => {
          if (videoRef.value) {
            try { videoRef.value.pause() } catch (e) {}
            try { videoRef.value.currentTime = 0 } catch (e) {}
            isPlaying.value = false
            currentTime.value = 0
            duration.value = 0
            videoDuration.value = 0
            try { if (typeof videoRef.value.load === 'function') videoRef.value.load() } catch (e) {}
          }
        })
      }
    } else {
      console.error('获取视频地址失败:', res)
      message.error('获取视频地址失败')
    }
  }).catch(err => {
    console.error('获取视频地址异常:', err)
    message.error('获取视频地址异常')
  })
}

// 下拉切换视频类型
function handleVideoTypeChange({ key }) {
  const val = parseInt(key, 10)
  if (Number.isNaN(val)) return
  selectedVideoType.value = val
  // 切换类型时退出临时配音
  isDubbingTemp.value = false
  // 清空字幕展示/选中
  clearSubtitleDisplaySelection()
  
  // 如果是完整版本，直接应用；否则检查缓存，没有则调用接口
  if (val === YoutubeVideoTypeObject.full) {
    applyCurrentVideoSource()
  } else {
    // 检查缓存中是否有该类型的URL
    if (videoUrlCache.value[val]) {
      applyCurrentVideoSource()
    } else {
      // 调用接口获取视频URL
      getVideoTypeUrl(val)
    }
  }
}

// 切换配音源
function toggleDubbing() {
  // 仅切换临时配音状态，不改动左侧下拉选择
  isDubbingTemp.value = !isDubbingTemp.value
  // 清空字幕展示/选中
  clearSubtitleDisplaySelection()
  applyCurrentVideoSource()
}

// 点击左侧按钮时，按顺序在非配音类型间循环切换
function handleVideoTypeToggle() {
  // 不改变当前下拉已选类型，仅根据已选类型重新应用视频源
  // 同时关闭临时配音，回到该类型对应的视频地址
  isDubbingTemp.value = false
  // 清空字幕展示/选中
  clearSubtitleDisplaySelection()
  applyCurrentVideoSource()
}
// 浅克隆字幕数组，避免引用同一对象导致编辑联动
function cloneSubtitlesData(subtitles) {
  if (!Array.isArray(subtitles)) {
    return []
  }
  return subtitles.map((item) => ({
    ...item
  }))
}

// 重置“当前字幕状态”（用于切换集数或关闭弹窗时）
function resetSubtitleState() {
  currentSubtitle.value = null
  currentOriginalSubtitle.value = null
  displaySubtitle.value = null
  displayOriginalSubtitle.value = null
  forcedSubtitle.value = null
  forcedOriginalSubtitle.value = null
}

// 加载指定集（刷新视频地址、字幕与 UI 状态）
function loadEpisode(episode) {
  if (!episode) {
    return
  }

  stopPreciseTimeUpdate()

  currentEpisode.value = episode
  if (episode.episodeNumber) {
    selectedEpisodeNumber.value = episode.episodeNumber
  }

  // 克隆字幕，确保各集互不干扰
  const originalData = cloneSubtitlesData(episode.originalSubtitles)
  const translatedData = cloneSubtitlesData(episode.translatedSubtitles)

  resetSubtitleState()

  originalSubtitles.value = originalData
  translatedSubtitles.value = translatedData

  // 根据当前选择应用视频源
  applyCurrentVideoSource()

  if (videoRef.value) {
    try {
      videoRef.value.pause()
    } catch (error) {
      console.warn('暂停视频失败:', error)
    }
    try {
      videoRef.value.currentTime = 0
    } catch (error) {
      console.warn('重置播放时间失败:', error)
    }
    try {
      if (typeof videoRef.value.load === 'function') {
        videoRef.value.load()
      }
    } catch (error) {
      console.warn('重载视频元素失败:', error)
    }
  }

  isPlaying.value = false
  currentTime.value = 0
  duration.value = 0
  videoDuration.value = 0

  nextTick(() => {
    translatedSubtitles.value.forEach((item, index) => {
      validateSubtitleRed(item, index)
    })

    updateOriginalSubtitleStyle()
    updateSubtitleStyle()
    updateSubtitlePrecise()

    if (translatedSubtitles.value.length > 0) {
      scrollTranslatedSubtitleIntoView(0, 'auto')
    }
  })
}

// 集数下拉切换回调
function handleEpisodeChange(value) {
  if (!value) {
    return
  }
  // 切换集时，重新获取集详情
  getEqDetail(value)
}

// 处理字幕数据，确保时间格式正确
function processSubtitleData(subtitles) {
  if (!Array.isArray(subtitles)) {
    return []
  }
  
  return subtitles.map(item => {
    const processed = { ...item }
    
    // 如果存在 startSeconds 但没有 startTime，转换时间格式
    if (processed.startSeconds !== undefined && !processed.startTime) {
      processed.startTime = secondsToTime(processed.startSeconds)
    }
    // 如果存在 endSeconds 但没有 endTime，转换时间格式
    if (processed.endSeconds !== undefined && !processed.endTime) {
      processed.endTime = secondsToTime(processed.endSeconds)
    }
    // 如果存在 startTime 但没有 startSeconds，转换秒数
    if (processed.startTime && processed.startSeconds === undefined) {
      processed.startSeconds = timeToSeconds(processed.startTime)
    }
    // 如果存在 endTime 但没有 endSeconds，转换秒数
    if (processed.endTime && processed.endSeconds === undefined) {
      processed.endSeconds = timeToSeconds(processed.endTime)
    }
    
    return processed
  })
}

// 获取集详情
function getEqDetail(eqNumber) {
  isLoading.value = true
  
  // 清空视频URL缓存
  videoUrlCache.value = {
    [YoutubeVideoTypeObject.full]: '',
    [YoutubeVideoTypeObject.no_bgm]: '',
    [YoutubeVideoTypeObject.no_subtitle]: '',
    dubbing: ''
  }
  
  const params = {
    task_id: eqDetail.value.task_id,
    eq_number: eqNumber
  }
  
  taskEqDetail(params).then(res => {
    const { code, data } = res
    if (code === 0) {
      console.log('集详情:', data)
      detail.value = data || {}
      
      // 更新下拉框选中状态
      selectedEpisodeNumber.value = eqNumber
      
      // 初始化视频URL缓存
      videoUrlCache.value = {
        [YoutubeVideoTypeObject.full]: data.video_url || '', // 完整版本
        [YoutubeVideoTypeObject.no_bgm]: '', // 无BGM版本（需要调用接口获取）
        [YoutubeVideoTypeObject.no_subtitle]: '', // 无字幕版本（需要调用接口获取）
        dubbing: data.output_video_url || '' // 配音版本
      }
      
      // 处理字幕数据
      const inputSubtitles = data.input_subtitle || []
      const outputSubtitles = data.output_subtitle || []
      
      // 处理并克隆原字幕
      originalSubtitles.value = processSubtitleData(inputSubtitles)
      
      // 处理并克隆翻译字幕
      translatedSubtitles.value = processSubtitleData(outputSubtitles)
      
      // 重置字幕状态
      resetSubtitleState()
      
      // 重置编辑状态（加载新的集时重置）
      hasEditedSubtitle.value = false
      
      // 切换集数后，默认选中完整版本视频
      selectedVideoType.value = YoutubeVideoTypeObject.full
      isDubbingTemp.value = false
      
      // 应用视频源
      applyCurrentVideoSource()
      
      // 验证翻译字幕的红色标记
      nextTick(() => {
        translatedSubtitles.value.forEach((item, index) => {
          validateSubtitleRed(item, index)
        })
        
        updateOriginalSubtitleStyle()
        updateSubtitleStyle()
        updateSubtitlePrecise()
        
        // 滚动到第一条字幕
        if (translatedSubtitles.value.length > 0) {
          scrollTranslatedSubtitleIntoView(0, 'auto')
        }
      })
    } else {
      console.error('获取集详情失败:', res)
      message.error(res.message || '获取集详情失败')
    }
  }).catch(err => {
    console.error('获取集详情异常:', err)
    message.error('获取集详情异常')
  }).finally(() => {
    isLoading.value = false
  })
}

/**
 * 验证时间格式是否为 xx:xx:xx,xxx
 * @param {string} timeStr - 时间字符串
 * @returns {boolean} 是否符合格式
 */
function isTimeFormatValid(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') {
    return false
  }
  // 格式：xx:xx:xx,xxx（两位小时:两位分钟:两位秒,三位毫秒）
  const timeFormatRegex = /^\d{2}:\d{2}:\d{2},\d{3}$/
  return timeFormatRegex.test(timeStr)
}

/**
 * 将时间字符串转换为秒数（支持毫秒）
 * @param {string} timeStr - 时间字符串，格式：HH:MM:SS,mmm
 * @returns {number} 秒数（包含毫秒）
 */
function timeToSeconds(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') {
    return 0
  }
  try {
    const [time, ms] = timeStr.split(',')
    const [hours, minutes, seconds] = time.split(':').map(Number)
    return hours * 3600 + minutes * 60 + seconds + Number(ms) / 1000
  } catch (error) {
    console.error('时间转换失败:', timeStr, error)
    return 0
  }
}

/**
 * 将秒数转换为时间字符串（支持毫秒）
 * @param {number} seconds - 秒数（包含毫秒）
 * @returns {string} 时间字符串，格式：HH:MM:SS,mmm
 */
function secondsToTime(seconds) {
  if (typeof seconds !== 'number' || isNaN(seconds)) {
    return '00:00:00,000'
  }
  
  const totalSeconds = Math.floor(seconds)
  const ms = Math.round((seconds - totalSeconds) * 1000)
  
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`
}

// 计算语速：字幕字符串长度 / (结束时间 - 开始时间)
function calculateSpeed(startSeconds, endSeconds, text) {
  if (!startSeconds || !endSeconds) {
    return '0.00'
  }
  // 如果文本为空，返回 0.00（需要标红）
  if (!text || text.length === 0) {
    return '0.00'
  }
  const duration = endSeconds - startSeconds
  // 如果时长为0或负数，返回0.00
  if (duration <= 0) {
    return '0.00'
  }
  const textLength = text.length
  const speed = (textLength / duration).toFixed(2)
  return speed
}

// 时间输入框输入变化回调（实时判断和计算语速）
function handleTimeInput(event, item, field, index) {
  const timeValue = item[field]
  
  // 如果格式不正确，不进行判断（格式验证在失焦时处理）
  if (!isTimeFormatValid(timeValue)) {
    return
  }
  
  // 将时间字符串转换为秒数（用于判断和实时更新）
  const seconds = timeToSeconds(timeValue)
  
  // 实时更新秒数
  if (field === 'startTime') {
    item.startSeconds = seconds
  } else if (field === 'endTime') {
    item.endSeconds = seconds
  }
  
  // 实时计算语速
  updateSpeedRed(item)
  
  // 实时验证当前项和相邻项的规则
  // 验证当前项
  checkEndTimeRed(item, index)
  
  // 验证上一项（如果输入的是开始时间）
  if (field === 'startTime' && index > 0) {
    const prevItem = translatedSubtitles.value[index - 1]
    if (prevItem && prevItem.endSeconds) {
      const prevEndTimeMs = Math.round(prevItem.endSeconds * 10000)
      const currentStartTimeMs = Math.round(seconds * 10000)
      if (prevEndTimeMs > currentStartTimeMs) {
        prevItem.endTimeRed = true
      } else {
        // 需要重新验证上一项的所有规则
        checkEndTimeRed(prevItem, index - 1)
      }
    }
  }
}

// 时间输入框失焦回调（转换秒数，语速已在输入时计算）
function handleTimeBlur(event, item, field, index) {
  const timeValue = item[field]
  
  // 标记已编辑
  hasEditedSubtitle.value = true
  
  // 验证时间格式
  if (!isTimeFormatValid(timeValue)) {
    // 格式不正确，不更新秒数，但需要标记为红色
    validateSubtitleRed(item, index)
    return
  }
  
  // 将时间字符串转换为秒数并更新（如果输入时没有更新）
  const seconds = timeToSeconds(timeValue)
  
  if (field === 'startTime') {
    item.startSeconds = seconds
  } else if (field === 'endTime') {
    item.endSeconds = seconds
  }
  
  // 重新验证当前项和相邻项的所有规则
  nextTick(() => {
    // 验证当前项
    validateSubtitleRed(item, index)
    
    // 验证上一项（如果存在）
    if (index > 0 && translatedSubtitles.value[index - 1]) {
      validateSubtitleRed(translatedSubtitles.value[index - 1], index - 1)
    }
    
    // 验证下一项（如果存在）
    if (index < translatedSubtitles.value.length - 1 && translatedSubtitles.value[index + 1]) {
      validateSubtitleRed(translatedSubtitles.value[index + 1], index + 1)
    }
  })
}

// 原字幕文本输入变化回调
function handleOriginalTextInput(event, index) {
  if (originalSubtitles.value[index]) {
    originalSubtitles.value[index].text = event.target.value
  }
  hasEditedSubtitle.value = true
}

// 字幕文本输入变化回调（实时计算语速）
function handleTextInput(event, item, index) {
  // 实时计算语速
  updateSpeedRed(item)
}

// 字幕文本失焦回调
function handleTextBlur(event, item, index) {
  // 标记已编辑
  hasEditedSubtitle.value = true
  
  // 重新验证当前项的所有规则（主要是语速）
  validateSubtitleRed(item, index)
}

/**
 * 判断是否显示分割线按钮
 * 规则：
 * 1. 如果 index > 0，显示向上合并按钮
 * 2. 如果不是最后一行，显示添加按钮
 * 3. 如果是最后一行，且结束时间小于视频总时长，显示添加按钮
 */
function shouldShowDividerButtons(item, index) {
  // 如果不是第一行，可以向上合并
  if (index > 0) {
    return true
  }
  // 如果不是最后一行，显示按钮
  if (index < translatedSubtitles.value.length - 1) {
    return true
  }
  
  // 如果是最后一行，判断结束时间是否小于视频总时长
  if (videoDuration.value > 0 && item.endSeconds) {
    return item.endSeconds < videoDuration.value
  }
  
  // 默认不显示
  return false
}

// 向上合并字幕（将当前行合并到上一行，然后删除当前行）
function handleMergeSubtitle(index) {
  if (index <= 0) {
    return // 第一行，无法向上合并
  }

  const currentItem = translatedSubtitles.value[index]
  const prevItem = translatedSubtitles.value[index - 1]

  if (!currentItem || !prevItem) {
    return
  }

  // 合并原字幕到上一条
  const prevOriginal = originalSubtitles.value[index - 1]
  const currentOriginal = originalSubtitles.value[index]
  if (prevOriginal && currentOriginal) {
    prevOriginal.text = (prevOriginal.text || '') + (currentOriginal.text || '')
    prevOriginal.endTime = currentOriginal.endTime
    prevOriginal.endSeconds = currentOriginal.endSeconds
  }

  // 合并翻译字幕到上一条
  prevItem.text = (prevItem.text || '') + (currentItem.text || '')
  prevItem.endTime = currentItem.endTime
  prevItem.endSeconds = currentItem.endSeconds

  // 标记已编辑
  hasEditedSubtitle.value = true

  // 调用删除方法删除当前字幕
  handleDeleteSubtitle(index)
}

/**
 * 检查结束时间是否需要标红
 */
function checkEndTimeRed(item, index) {
  if (!item.startSeconds || !item.endSeconds) {
    item.endTimeRed = false
    return
  }
  
  // 检查：结束时间 - 开始时间 < 0.5 秒
  const startTimeMs = Math.round(item.startSeconds * 10000)
  const endTimeMs = Math.round(item.endSeconds * 10000)
  if (endTimeMs - startTimeMs < 5000) {
    item.endTimeRed = true
    return
  }
  
  // 检查：结束时间 > 下一个开始时间
  if (index < translatedSubtitles.value.length - 1) {
    const nextItem = translatedSubtitles.value[index + 1]
    if (nextItem && nextItem.startSeconds) {
      const nextStartTimeMs = Math.round(nextItem.startSeconds * 10000)
      if (endTimeMs > nextStartTimeMs) {
        item.endTimeRed = true
        return
      }
    }
  }
  
  item.endTimeRed = false
}

/**
 * 创建新字幕（通用方法，支持带文案和不带文案）
 * @param {number} index - 当前字幕的索引
 * @param {string} text - 新字幕的文本内容（可选，默认为空字符串）
 * @returns {Object|null} 返回新创建的字幕对象，如果创建失败返回 null
 */
function createNewSubtitle(index, text = '') {
  const currentItem = translatedSubtitles.value[index]
  
  if (!currentItem || !currentItem.endSeconds) {
    console.warn('无法添加字幕：当前项缺少结束时间')
    return null
  }
  
  // 获取当前行的结束时间
  const currentEndSeconds = currentItem.endSeconds
  const currentEndTime = currentItem.endTime
  
  // 获取下一行的开始时间（如果存在）
  // 如果是最后一行，则使用视频总时长作为下一行的开始时间
  let nextStartSeconds = null
  let nextStartTime = null
  if (index < translatedSubtitles.value.length - 1) {
    // 不是最后一行，获取下一行的开始时间
    const nextItem = translatedSubtitles.value[index + 1]
    if (nextItem && nextItem.startSeconds) {
      nextStartSeconds = nextItem.startSeconds
      nextStartTime = nextItem.startTime
    }
  } else {
    // 是最后一行，使用视频总时长
    nextStartSeconds = videoDuration.value
    nextStartTime = secondsToTime(videoDuration.value)
  }
  
  // 新字幕的开始时间 = 当前行的结束时间
  const newStartSeconds = currentEndSeconds
  const newStartTime = currentEndTime
  
  // 新字幕的结束时间 = 开始时间 + 2秒
  let newEndSeconds = newStartSeconds + 2
  
  // 判断：如果新字幕的结束时间 > 下一行的开始时间（或视频总时长），则新字幕的结束时间 = 下一行的开始时间
  if (nextStartSeconds !== null && newEndSeconds > nextStartSeconds) {
    newEndSeconds = nextStartSeconds
  }
  
  // 将秒数转换为时间字符串
  const newEndTime = secondsToTime(newEndSeconds)
  
  // 创建新的字幕对象
  const newSubtitle = {
    startTime: newStartTime,
    startSeconds: newStartSeconds,
    endTime: newEndTime,
    endSeconds: newEndSeconds,
    text: text, // 支持传入文本内容
    endTimeRed: false,
    speedRed: false
  }
  
  // 插入到当前行和下一行之间
  translatedSubtitles.value.splice(index + 1, 0, newSubtitle)
  
  // 立即验证新字幕的所有规则（不使用 nextTick，确保第一时间显示红色）
  validateSubtitleRed(newSubtitle, index + 1)
  
  return newSubtitle
}

// 添加字幕（点击+号，调用接口新增）
function handleAddSubtitle(index) {
  const currentItem = translatedSubtitles.value[index]
  if (!currentItem) {
    message.warning('当前字幕数据异常')
    return
  }
  if (!eqDetail.value.task_id || !selectedEpisodeNumber.value) {
    message.warning('缺少必要参数，无法新增字幕')
    return
  }

  const startSeconds = currentItem.endSeconds
  const endSeconds = startSeconds + 0.5

  const params = {
    task_id: eqDetail.value.task_id,
    eq_number: selectedEpisodeNumber.value,
    start_seconds: startSeconds,
    end_seconds: endSeconds,
    speaker_id: currentItem.speakerId || ''
  }

  isLoading.value = true
  taskCreateSubtitle(params).then(res => {
    const { code, data, message: msg } = res
    if (code === 0 && data) {
      const newOriginal = processSubtitleData([data.input_subtitle || {}])[0]
      const newTranslated = processSubtitleData([data.output_subtitle || {}])[0]
      if (newOriginal) {
        originalSubtitles.value.splice(index + 1, 0, newOriginal)
      }
      if (newTranslated) {
        translatedSubtitles.value.splice(index + 1, 0, newTranslated)
        nextTick(() => {
          validateSubtitleRed(newTranslated, index + 1)
        })
      }
      message.success('新增字幕成功')
    } else {
      message.error(msg || '新增字幕失败')
    }
  }).catch(err => {
    console.error('新增字幕异常:', err)
    message.error(err.message || '新增字幕异常')
  }).finally(() => {
    isLoading.value = false
  })
}

// 删除字幕（点击删除按钮，调用接口删除）
function handleDeleteSubtitle(index) {
  const currentItem = translatedSubtitles.value[index]
  if (!currentItem) {
    message.warning('当前字幕数据异常')
    return
  }
  if (!eqDetail.value.task_id || !selectedEpisodeNumber.value) {
    message.warning('缺少必要参数，无法删除字幕')
    return
  }
  if (!currentItem.id) {
    message.warning('当前字幕缺少ID，无法删除')
    return
  }

  const params = {
    task_id: eqDetail.value.task_id,
    eq_number: selectedEpisodeNumber.value,
    utterance_id: currentItem.id
  }

  isLoading.value = true
  taskRemoveSubtitle(params).then(res => {
    const { code, message: msg } = res
    if (code === 0) {
      translatedSubtitles.value.splice(index, 1)
      originalSubtitles.value.splice(index, 1)
      nextTick(() => {
        translatedSubtitles.value.forEach((item, idx) => {
          validateSubtitleRed(item, idx)
        })
      })
      message.success('删除字幕成功')
    } else {
      message.error(msg || '删除字幕失败')
    }
  }).catch(err => {
    console.error('删除字幕异常:', err)
    message.error(err.message || '删除字幕异常')
  }).finally(() => {
    isLoading.value = false
  })
}

// 处理文本输入框回车事件（分割字幕）
function handleTextKeydown(event, item, index) {
  if (event.key === 'Enter') {
    event.preventDefault()
  }
}

/**
 * 更新语速标红状态
 * 规则：语速 < 0 或 > detail.value.speech_speed 时变红
 */
function updateSpeedRed(item) {
  // 如果没有开始时间或结束时间，不标红
  if (!item.startSeconds || !item.endSeconds) {
    item.speedRed = false
    return
  }
  
  // 即使文本为空，也要计算语速（会返回 0.00）
  const speed = calculateSpeed(item.startSeconds, item.endSeconds, item.text)
  const speedValue = parseFloat(speed)
  
  // 获取最大语速限制，如果没有值则默认为 0
  const maxSpeed = detail.value?.speech_speed || 0
  
  // 语速 < 0 或 > maxSpeed 时变红
  if (speedValue < 0 || speedValue > maxSpeed) {
    item.speedRed = true
  } else {
    item.speedRed = false
  }
}

/**
 * 重新计算字幕对象的标红状态（公共函数）
 * 包括时间格式验证、结束时间规则、开始时间规则、语速规则
 * @param {Object} item - 字幕对象
 * @param {number} index - 字幕在数组中的索引
 */
function validateSubtitleRed(item, index) {
  if (!item) {
    return
  }
  
  // 初始化 endTimeRed 和 speedRed 字段
  if (item.endTimeRed === undefined) {
    item.endTimeRed = false
  }
  if (item.speedRed === undefined) {
    item.speedRed = false
  }
  
  // 1. 验证时间格式（如果格式不正确，直接标记为红色，不继续验证其他规则）
  const startTimeValid = isTimeFormatValid(item.startTime)
  const endTimeValid = isTimeFormatValid(item.endTime)
  
  if (!startTimeValid || !endTimeValid) {
    item.endTimeRed = true
    // 时间格式不正确时，仍然需要验证语速（如果时间秒数存在）
    if (item.startSeconds && item.endSeconds) {
      updateSpeedRed(item)
    } else {
      item.speedRed = false
    }
    return
  }
  
  // 2. 验证结束时间规则（结束时间 <= 开始时间 或 结束时间 > 下一个开始时间）
  checkEndTimeRed(item, index)
  
  // 3. 验证开始时间规则：如果上一个结束时间 > 当前开始时间，则上一个结束时间变红
  if (item.startSeconds && index > 0) {
    const prevItem = translatedSubtitles.value[index - 1]
    if (prevItem && prevItem.endSeconds) {
      const prevEndTimeMs = Math.round(prevItem.endSeconds * 10000)
      const currentStartTimeMs = Math.round(item.startSeconds * 10000)
      if (prevEndTimeMs > currentStartTimeMs) {
        prevItem.endTimeRed = true
      }
    }
  }
  
  // 4. 验证语速规则
  updateSpeedRed(item)
}

// 切换说话人
function handleSpeakerChange(speakerId, item, index) {
  item.speakerId = speakerId
  hasEditedSubtitle.value = true
}

// 翻译单条原字幕
function handleTranslateSubtitle(index) {
  const origItem = originalSubtitles.value[index]
  if (!origItem || !origItem.text) {
    message.warning('原字幕内容为空，无法翻译')
    return
  }
  if (origItem._translating) return

  origItem._translating = true
  taskTranslateSubtitleText({
    task_id: eqDetail.value.task_id,
    eq_number: selectedEpisodeNumber.value,
    text: origItem.text
  }).then(res => {
    const { code, data, message: msg } = res
    if (code === 0 && data?.translation) {
      const transItem = translatedSubtitles.value[index]
      if (transItem) {
        transItem.text = data.translation
        hasEditedSubtitle.value = true
        message.success('翻译成功')
      }
    } else {
      message.error(msg || '翻译失败')
    }
  }).catch(err => {
    console.error('翻译异常:', err)
    message.error(err.message || '翻译异常')
  }).finally(() => {
    origItem._translating = false
  })
}

// 显示
function showEdit (drama, record) {
  visible.value = true
  dramaDetail.value = drama || {}
  eqDetail.value = record || {}

  // 初始化/重置剧集选择状态
  // 重置集数信息
  episodes.value = []
  seriesInfo.value = null
  currentEpisode.value = null
  selectedEpisodeNumber.value = null
  
  // 清空视频播放地址，由集数据决定
  videoUrl.value = ''

  // 使用 dramaDetail.value.total_ep 初始化集列表
  const totalEp = dramaDetail.value.total_ep || 0
  if (totalEp > 0) {
    // 生成集列表：1 到 total_ep
    episodes.value = Array.from({ length: totalEp }, (_, index) => ({
      episodeNumber: index + 1
    }))
  }
  
  // 使用 eqDetail.value.eq_number 初始化集列表选中
  const initialEqNumber = eqDetail.value.eq_number || 1
  selectedEpisodeNumber.value = initialEqNumber
  
  // 获取集详情
  getEqDetail(initialEqNumber)
  
  // 等待DOM更新后初始化
  nextTick(() => {
    // 初始化字幕样式
    if (videoRef.value) {
      updateSubtitleStyle()
      updateOriginalSubtitleStyle()
      
      // 使用 ResizeObserver 监听视频元素尺寸变化
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          updateSubtitleStyle()
          updateOriginalSubtitleStyle()
        })
        resizeObserver.observe(videoRef.value)
      }
      
      // 监听窗口大小变化，更新字幕样式
      const handleResize = () => {
        updateSubtitleStyle()
        updateOriginalSubtitleStyle()
      }
      window.addEventListener('resize', handleResize)
    }
  })
}


// 关闭
function onClose () {
  visible.value = false
  
  // 重置剧集和集相关
  dramaDetail.value = {}
  eqDetail.value = {}
  detail.value = null
  
  // 重置集数列表和选中状态
  episodes.value = []
  seriesInfo.value = null
  currentEpisode.value = null
  selectedEpisodeNumber.value = null
  
  // 重置字幕列表
  originalSubtitles.value = []
  translatedSubtitles.value = []
  
  // 重置编辑状态
  hasEditedSubtitle.value = false
  
  // 重置视频相关
  videoUrl.value = ''
  videoUrlCache.value = {
    [YoutubeVideoTypeObject.full]: '',
    [YoutubeVideoTypeObject.no_bgm]: '',
    [YoutubeVideoTypeObject.no_subtitle]: '',
    dubbing: ''
  }
  selectedVideoType.value = YoutubeVideoTypeObject.full
  isDubbingTemp.value = false
  currentTime.value = 0
  duration.value = 0
  videoDuration.value = 0
  isPlaying.value = false
  playbackRate.value = 1
  
  showSubtitle.value = true
  showOriginalSubtitle.value = true
  
  // 停止精确时间更新
  stopPreciseTimeUpdate()
  
  // 断开 ResizeObserver
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  
  // 重置字幕显示相关
  currentSubtitle.value = null
  currentOriginalSubtitle.value = null
  displaySubtitle.value = null
  displayOriginalSubtitle.value = null
  forcedSubtitle.value = null
  forcedOriginalSubtitle.value = null
  subtitleStyle.value = {}
  originalSubtitleStyle.value = {}
}

// 监听当前翻译字幕变化，自动滚动至可视区域
watch(currentSubtitle, (newSubtitle) => {
  if (!newSubtitle) {
    return
  }
  const index = translatedSubtitles.value.findIndex((item) => item === newSubtitle)
  if (index !== -1) {
    scrollTranslatedSubtitleIntoView(index)
  }
})

// ========== 视频控制相关函数 ==========

// 获取视频元素
function getVideoElement() {
  if (videoRef.value) {
    return videoRef.value
  }
  return null
}

// 视频加载完成
function handleVideoLoaded() {
  if (videoRef.value) {
    try {
      videoRef.value.playbackRate = playbackRate.value
      const videoDurationValue = videoRef.value.duration || 0
      duration.value = videoDurationValue
      // 更新视频总时长（用于字幕判断）
      if (videoDurationValue > 0) {
        videoDuration.value = videoDurationValue
        console.log('视频总时长已更新:', videoDurationValue, '秒')
      }
      // 更新字幕样式
      nextTick(() => {
        updateSubtitleStyle()
        updateOriginalSubtitleStyle()
      })
    } catch (error) {
      console.error('设置倍速失败:', error)
    }
  }
}

// 视频时长变化
function handleDurationChange() {
  if (videoRef.value) {
    const videoDurationValue = videoRef.value.duration || 0
    duration.value = videoDurationValue
    // 更新视频总时长（用于字幕判断）
    if (videoDurationValue > 0) {
      videoDuration.value = videoDurationValue
      console.log('视频总时长已更新:', videoDurationValue, '秒')
    }
    // 视频尺寸可能已确定，更新字幕样式
    nextTick(() => {
      updateSubtitleStyle()
      updateOriginalSubtitleStyle()
    })
  }
}

// 视频时间更新
function handleTimeUpdate() {
  if (videoRef.value) {
    currentTime.value = videoRef.value.currentTime || 0
  }
}

// 播放/暂停切换
async function togglePlayPause() {
  const video = getVideoElement()
  if (!video) {
    console.warn('视频元素未加载')
    return
  }
  
  try {
    if (video.paused) {
      await video.play()
      isPlaying.value = true
    } else {
      video.pause()
      isPlaying.value = false
    }
  } catch (error) {
    console.error('播放/暂停操作失败:', error)
  }
}

// 处理播放事件
function handlePlay() {
  isPlaying.value = true
  // 开始精确时间更新
  startPreciseTimeUpdate()
}

// 处理暂停事件
function handlePause() {
  isPlaying.value = false
  // 停止精确时间更新
  stopPreciseTimeUpdate()
  // 暂停时更新一次字幕
  updateSubtitlePrecise()
}

// 后退0.5秒
function seekBackward() {
  const video = getVideoElement()
  if (!video) {
    console.warn('视频元素未加载')
    return
  }
  
  try {
    const newTime = Math.max(0, (video.currentTime || 0) - 0.5)
    video.currentTime = newTime
    currentTime.value = newTime
    // 跳转后立即更新字幕
    nextTick(() => {
      updateSubtitlePrecise()
    })
  } catch (error) {
    console.error('后退失败:', error)
  }
}

// 前进0.5秒
function seekForward() {
  const video = getVideoElement()
  if (!video) {
    console.warn('视频元素未加载')
    return
  }
  
  try {
    const videoDuration = video.duration || 0
    const newTime = Math.min(videoDuration, (video.currentTime || 0) + 0.5)
    video.currentTime = newTime
    currentTime.value = newTime
    // 跳转后立即更新字幕
    nextTick(() => {
      updateSubtitlePrecise()
    })
  } catch (error) {
    console.error('前进失败:', error)
  }
}

// 修改播放速度
function handlePlaybackRateChange({ key }) {
  const rate = parseFloat(key)
  if (isNaN(rate)) return
  
  const video = getVideoElement()
  if (!video) {
    console.warn('视频元素未加载')
    return
  }
  
  try {
    playbackRate.value = rate
    video.playbackRate = rate
  } catch (error) {
    console.error('修改倍速失败:', error)
  }
}

// 进度条点击
function handleProgressClick(event) {
  // 如果正在拖拽，不处理点击事件
  if (isDragging) {
    return
  }
  
  const video = getVideoElement()
  if (!video || !duration.value) {
    return
  }
  
  const progressBar = event.currentTarget
  const rect = progressBar.getBoundingClientRect()
  const clickX = event.clientX - rect.left
  const percent = clickX / rect.width
  const newTime = percent * duration.value
  
  try {
    video.currentTime = Math.max(0, Math.min(newTime, duration.value))
    currentTime.value = video.currentTime
    // 跳转后立即更新字幕（使用精确更新）
    nextTick(() => {
      updateSubtitlePrecise()
    })
  } catch (error) {
    console.error('跳转失败:', error)
  }
}

// 进度条拖拽
let isDragging = false

function handleProgressMouseDown(event) {
  event.preventDefault()
  event.stopPropagation()
  
  const video = getVideoElement()
  if (!video || !duration.value) {
    return
  }
  
  isDragging = true
  const progressBar = event.currentTarget
  const rect = progressBar.getBoundingClientRect()
  
  const updateProgress = (e) => {
    const clientX = e.clientX || e.touches?.[0]?.clientX
    if (!clientX) return
    
    const clickX = clientX - rect.left
    const percent = Math.max(0, Math.min(1, clickX / rect.width))
    const newTime = percent * duration.value
    
    try {
      video.currentTime = newTime
      currentTime.value = newTime
      // 拖拽时实时更新字幕
      updateSubtitlePrecise()
    } catch (error) {
      console.error('拖拽失败:', error)
    }
  }
  
  // 立即更新一次
  updateProgress(event)
  
  // 添加全局事件监听
  const handleMouseMove = (e) => {
    if (isDragging) {
      updateProgress(e)
    }
  }
  
  const handleMouseUp = () => {
    isDragging = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('touchmove', handleMouseMove)
    document.removeEventListener('touchend', handleMouseUp)
  }
  
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  document.addEventListener('touchmove', handleMouseMove)
  document.addEventListener('touchend', handleMouseUp)
}

// 字幕显示隐藏切换
function toggleSubtitle() {
  showSubtitle.value = !showSubtitle.value
  console.log('字幕显示状态:', showSubtitle.value ? '显示' : '隐藏')
}

// 原版字幕显示隐藏切换
function toggleOriginalSubtitleVisibility() {
  showOriginalSubtitle.value = !showOriginalSubtitle.value
  nextTick(() => {
    updateSubtitleStyle()
  })
}

/**
 * 滚动翻译字幕项到可视区域
 * @param {number} index - 字幕索引
 * @param {'smooth' | 'auto'} behavior - 滚动行为
 */
function scrollTranslatedSubtitleIntoView(index, behavior = 'smooth') {
  if (index === undefined || index === null || index < 0) {
    return
  }

  nextTick(() => {
    const container = subtitleContainerRef.value
    if (!container) {
      return
    }

    const element = document.getElementById(`translated-subtitle-${index}`)
    if (!element) {
      return
    }

    const elementTop = element.offsetTop
    const elementHeight = element.offsetHeight
    const containerHeight = container.clientHeight
    const scrollTop = container.scrollTop
    const elementBottom = elementTop + elementHeight
    const padding = 40

    if (elementTop < scrollTop + padding) {
      const targetTop = Math.max(elementTop - padding, 0)
      container.scrollTo({ top: targetTop, behavior })
    } else if (elementBottom > scrollTop + containerHeight - padding) {
      const targetTop = elementBottom - containerHeight + padding
      container.scrollTo({ top: Math.max(targetTop, 0), behavior })
    }
  })
}

// 点击字幕行，定位到字幕开始时间并暂停播放
function handleSubtitleClick(item, index) {
  if (!item || item.startSeconds === undefined || item.startSeconds === null) {
    console.warn('无法定位：字幕缺少开始时间')
    return
  }
  
  if (!videoRef.value) {
    console.warn('视频元素未找到')
    return
  }
  
  // 设置视频播放时间到字幕开始时间
  videoRef.value.currentTime = item.startSeconds
  currentTime.value = item.startSeconds
  
  // 强制设置当前字幕为点击的字幕
  forcedSubtitle.value = item
  currentSubtitle.value = item
  displaySubtitle.value = item

  // 强制设置原版字幕：按 index 对应
  const matchedOriginal = originalSubtitles.value[index] || null
  forcedOriginalSubtitle.value = matchedOriginal
  currentOriginalSubtitle.value = matchedOriginal
  displayOriginalSubtitle.value = matchedOriginal
  
  // 暂停播放
  if (isPlaying.value) {
    videoRef.value.pause()
    isPlaying.value = false
    stopPreciseTimeUpdate()
  }
  
  // 更新字幕显示和位置（无论是否播放都需要更新）
  updateSubtitlePrecise()
  // 手动更新字幕样式，确保位置正确
  nextTick(() => {
    updateOriginalSubtitleStyle()
    updateSubtitleStyle()
  })
  
  console.log('定位到字幕开始时间:', item.startTime, item.startSeconds)
}

// ========== 字幕显示相关函数 ==========

// 计算视频实际显示区域（考虑 object-fit: contain）
function calculateVideoDisplayArea() {
  if (!videoRef.value) {
    return null
  }
  
  const video = videoRef.value
  const containerRect = video.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  // 获取视频原始尺寸
  const videoWidth = video.videoWidth
  const videoHeight = video.videoHeight
  
  // 如果视频尺寸还未加载，返回null
  if (!videoWidth || !videoHeight || videoWidth === 0 || videoHeight === 0) {
    return null
  }
  
  // 计算视频宽高比
  const videoAspectRatio = videoWidth / videoHeight
  const containerAspectRatio = containerWidth / containerHeight
  
  let displayWidth, displayHeight, offsetX, offsetY
  
  // 根据 object-fit: contain 计算实际显示区域
  if (videoAspectRatio > containerAspectRatio) {
    // 视频更宽，以宽度为准，上下会有黑边
    displayWidth = containerWidth
    displayHeight = containerWidth / videoAspectRatio
    offsetX = 0
    offsetY = (containerHeight - displayHeight) / 2
  } else {
    // 视频更高，以高度为准，左右会有黑边
    displayHeight = containerHeight
    displayWidth = containerHeight * videoAspectRatio
    offsetX = (containerWidth - displayWidth) / 2
    offsetY = 0
  }
  
  return {
    width: displayWidth,
    height: displayHeight,
    left: offsetX,
    top: offsetY,
    containerWidth,
    containerHeight
  }
}

// 更新字幕样式，限制在视频实际显示区域内
function updateSubtitleStyle() {
  const displayArea = calculateVideoDisplayArea()
  if (!displayArea || !videoRef.value) {
    subtitleStyle.value = {}
    return
  }
  
  // 获取 video 元素和 video-section 的位置信息
  const videoRect = videoRef.value.getBoundingClientRect()
  const videoSection = videoRef.value.parentElement
  if (!videoSection) {
    subtitleStyle.value = {}
    return
  }
  const videoSectionRect = videoSection.getBoundingClientRect()
  
  // 字幕最大宽度为视频实际显示宽度，不超过渲染宽度
  const maxWidth = displayArea.width
  
  // 计算字幕的left位置，使其居中在视频实际显示区域内
  // 视频实际显示区域的中心相对于 video-section 的位置
  // displayArea.left 是相对于 video 元素的，需要加上 video 在 section 中的位置
  const videoLeftInSection = videoRect.left - videoSectionRect.left
  const videoCenterX = videoLeftInSection + displayArea.left + displayArea.width / 2
  const videoSectionCenterX = videoSectionRect.width / 2
  const offsetX = videoCenterX - videoSectionCenterX
  
  // 计算字幕的bottom位置，翻译字幕显示在原版字幕上方
  // 翻译字幕的底部应该由原版字幕的顶部决定，并留出间距
  let bottomOffset = 0
  
  if (displayOriginalSubtitle.value && showOriginalSubtitle.value) {
    // 如果原版字幕存在，获取原版字幕覆盖层的实际位置
    // 使用 nextTick 确保原版字幕已经渲染
    nextTick(() => {
      // 重新获取所有必要的变量，因为它们可能已经变化
      const currentVideoSection = videoRef.value?.parentElement
      if (!currentVideoSection) {
        return
      }
      
      const originalOverlay = currentVideoSection.querySelector('.subtitle-overlay-original')
      if (originalOverlay) {
        const originalRect = originalOverlay.getBoundingClientRect()
        const currentVideoSectionRect = currentVideoSection.getBoundingClientRect()
        // 计算原版字幕顶部相对于 video-section 的位置
        const originalTopInSection = originalRect.top - currentVideoSectionRect.top
        // 翻译字幕的底部 = video-section 高度 - 原版字幕顶部在 section 中的位置 + 间距（例如 10px）
        // 这样翻译字幕的底部会在原版字幕顶部上方 10px 的位置
        const spacing = 10 // 两个字幕之间的间距
        const calculatedBottomOffset = currentVideoSectionRect.height - originalTopInSection + spacing
        
        // 重新计算 offsetX（因为可能已经变化）
        const currentVideoRect = videoRef.value.getBoundingClientRect()
        const currentVideoLeftInSection = currentVideoRect.left - currentVideoSectionRect.left
        const currentVideoCenterX = currentVideoLeftInSection + displayArea.left + displayArea.width / 2
        const currentVideoSectionCenterX = currentVideoSectionRect.width / 2
        const currentOffsetX = currentVideoCenterX - currentVideoSectionCenterX
        
        subtitleStyle.value = {
          maxWidth: `${maxWidth}px`,
          left: `calc(50% + ${currentOffsetX}px)`,
          transform: 'translateX(-50%)',
          bottom: `${calculatedBottomOffset}px`
        }
      } else {
        // 如果原版字幕元素不存在，使用默认位置（底部往上一点）
        const currentVideoRect = videoRef.value.getBoundingClientRect()
        const currentVideoSectionRect = currentVideoSection.getBoundingClientRect()
        const videoTopInSection = currentVideoRect.top - currentVideoSectionRect.top
        const displayBottomInVideo = displayArea.top + displayArea.height
        const displayBottomInSection = videoTopInSection + displayBottomInVideo
        const calculatedBottomOffset = currentVideoSectionRect.height - displayBottomInSection + 20
        
        // 重新计算 offsetX
        const currentVideoLeftInSection = currentVideoRect.left - currentVideoSectionRect.left
        const currentVideoCenterX = currentVideoLeftInSection + displayArea.left + displayArea.width / 2
        const currentVideoSectionCenterX = currentVideoSectionRect.width / 2
        const currentOffsetX = currentVideoCenterX - currentVideoSectionCenterX
        
        subtitleStyle.value = {
          maxWidth: `${maxWidth}px`,
          left: `calc(50% + ${currentOffsetX}px)`,
          transform: 'translateX(-50%)',
          bottom: `${calculatedBottomOffset}px`
        }
      }
    })
  } else {
    // 如果原版字幕不存在，使用默认位置（底部往上一点）
    const videoTopInSection = videoRect.top - videoSectionRect.top
    const displayBottomInVideo = displayArea.top + displayArea.height
    const displayBottomInSection = videoTopInSection + displayBottomInVideo
    bottomOffset = videoSectionRect.height - displayBottomInSection + 20
    
    subtitleStyle.value = {
      maxWidth: `${maxWidth}px`,
      left: `calc(50% + ${offsetX}px)`,
      transform: 'translateX(-50%)',
      bottom: `${bottomOffset}px`
    }
  }
}

// 更新原版字幕样式，限制在视频实际显示区域内
function updateOriginalSubtitleStyle() {
  const displayArea = calculateVideoDisplayArea()
  if (!displayArea || !videoRef.value) {
    originalSubtitleStyle.value = {}
    return
  }
  
  // 获取 video 元素和 video-section 的位置信息
  const videoRect = videoRef.value.getBoundingClientRect()
  const videoSection = videoRef.value.parentElement
  if (!videoSection) {
    originalSubtitleStyle.value = {}
    return
  }
  const videoSectionRect = videoSection.getBoundingClientRect()
  
  // 字幕最大宽度为视频实际显示宽度，不超过渲染宽度
  const maxWidth = displayArea.width
  
  // 计算字幕的left位置，使其居中在视频实际显示区域内
  const videoLeftInSection = videoRect.left - videoSectionRect.left
  const videoCenterX = videoLeftInSection + displayArea.left + displayArea.width / 2
  const videoSectionCenterX = videoSectionRect.width / 2
  const offsetX = videoCenterX - videoSectionCenterX
  
  // 原版字幕固定在底部（因为文案不会变化）
  const videoTopInSection = videoRect.top - videoSectionRect.top
  const displayBottomInVideo = displayArea.top + displayArea.height
  const displayBottomInSection = videoTopInSection + displayBottomInVideo
  // 往上偏移一点（例如 20px）
  const bottomOffset = videoSectionRect.height - displayBottomInSection + 20
  
  originalSubtitleStyle.value = {
    maxWidth: `${maxWidth}px`,
    left: `calc(50% + ${offsetX}px)`,
    transform: 'translateX(-50%)',
    bottom: `${bottomOffset}px`
  }
}

// 精确更新字幕（使用毫秒级精度）
function updateSubtitlePrecise() {
  if (!videoRef.value) {
    currentSubtitle.value = null
    currentOriginalSubtitle.value = null
    displaySubtitle.value = null
    displayOriginalSubtitle.value = null
    forcedSubtitle.value = null
    return
  }
  
  // 获取精确的当前播放时间（毫秒级精度）
  const preciseTime = videoRef.value.currentTime || 0
  currentTime.value = preciseTime
  
  // 如果有强制显示的字幕，检查是否还在其范围内
  if (forcedSubtitle.value) {
    const tolerance = 0.01
    const isInForcedRange = preciseTime >= (forcedSubtitle.value.startSeconds - tolerance) && 
                           preciseTime <= (forcedSubtitle.value.endSeconds + tolerance)
    
    if (isInForcedRange) {
      // 仍在强制字幕范围内，继续显示强制字幕
      currentSubtitle.value = forcedSubtitle.value
      displaySubtitle.value = forcedSubtitle.value
      // 强制字幕是翻译字幕，也需要查找对应的原版字幕
      if (forcedOriginalSubtitle.value) {
        currentOriginalSubtitle.value = forcedOriginalSubtitle.value
        displayOriginalSubtitle.value = forcedOriginalSubtitle.value
      } else {
        const forcedIndex = translatedSubtitles.value.indexOf(forcedSubtitle.value)
        const matchedOriginal = forcedIndex >= 0 ? (originalSubtitles.value[forcedIndex] || null) : null
        currentOriginalSubtitle.value = matchedOriginal
        displayOriginalSubtitle.value = matchedOriginal
      }
      // 更新原版字幕样式
      updateOriginalSubtitleStyle()
      return
    } else {
      // 超出强制字幕范围，清除强制标志，恢复正常流程
      forcedSubtitle.value = null
      forcedOriginalSubtitle.value = null
    }
  }
  
  // 更新翻译字幕显示（用于视频显示，按时间判断）
  if (translatedSubtitles.value.length > 0) {
    // 查找当前时间对应的字幕（使用精确的毫秒级比较）
    const subtitle = translatedSubtitles.value.find(
      (sub) => {
        const tolerance = 0.01
        return preciseTime >= (sub.startSeconds - tolerance) && 
               preciseTime <= (sub.endSeconds + tolerance)
      }
    )
    
    // 更新视频显示的字幕（按时间判断，时间过了就消失）
    displaySubtitle.value = subtitle || null
    
    // 更新列表高亮的字幕（只有找到新的字幕时才更新，否则保持当前字幕不变）
    if (subtitle) {
      currentSubtitle.value = subtitle
    }
    // 如果没有找到字幕，currentSubtitle 保持不变（保持高亮）
  } else {
    displaySubtitle.value = null
  }
  
  // 更新原版字幕显示（按翻译字幕 index 对应）
  if (currentSubtitle.value && originalSubtitles.value.length > 0) {
    const currentIndex = translatedSubtitles.value.indexOf(currentSubtitle.value)
    const originalSubtitle = currentIndex >= 0 ? (originalSubtitles.value[currentIndex] || null) : null
    displayOriginalSubtitle.value = originalSubtitle
    if (originalSubtitle) {
      currentOriginalSubtitle.value = originalSubtitle
    }
  } else {
    displayOriginalSubtitle.value = null
  }
  
  // 更新原版字幕样式（先更新原版字幕，因为翻译字幕位置依赖于它）
  updateOriginalSubtitleStyle()
  // 更新翻译字幕样式（翻译字幕位置依赖于原版字幕）
  nextTick(() => {
    updateSubtitleStyle()
  })
}

// 使用 requestAnimationFrame 进行精确的时间更新循环
function startPreciseTimeUpdate() {
  if (animationFrameId) {
    return // 已经在运行
  }
  
  const updateLoop = () => {
    if (!videoRef.value || videoRef.value.paused) {
      animationFrameId = null
      return
    }
    
    updateSubtitlePrecise()
    animationFrameId = requestAnimationFrame(updateLoop)
  }
  
  animationFrameId = requestAnimationFrame(updateLoop)
}

// 停止精确时间更新
function stopPreciseTimeUpdate() {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}

// 格式化时间显示（包含毫秒）
function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) {
    return '00:00:00,000'
  }
  
  const totalSeconds = Math.floor(seconds)
  const ms = Math.round((seconds - totalSeconds) * 1000)
  
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`
}


// 打开替换弹窗
function openReplaceModal() {
  replaceModalRef.value.show()
}

// 替换逻辑：按勾选范围遍历翻译字幕/原字幕进行全量替换与计数
function handleReplaceConfirm(payload) {
  if (!payload || payload.ok !== true) {
    if (payload?.reason === 'empty_find') {
      message.warning('请输入要查找的内容')
    } else if (payload?.reason === 'empty_target') {
      message.warning('请至少勾选一种字幕进行替换')
    }
    return
  }
  const findStr = payload.find
  const replaceStr = payload.replace ?? ''

  if (!findStr) {
    message.warning('请输入要查找的内容')
    return
  }

  let totalReplaced = 0
  const regex = new RegExp(findStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')

  // 替换翻译字幕
  if (payload.replaceTranslated) {
    translatedSubtitles.value.forEach((item, index) => {
      const src = item.text || ''
      const replaced = src.replace(regex, (m) => {
        totalReplaced += 1
        return replaceStr
      })
      if (replaced !== src) {
        item.text = replaced
        validateSubtitleRed(item, index)
      }
    })
  }

  // 替换原字幕（以翻译字幕索引为基准，取同索引原字幕）
  if (payload.replaceOriginal) {
    translatedSubtitles.value.forEach((_, index) => {
      const origItem = originalSubtitles.value[index]
      if (!origItem) return
      const src = origItem.text || ''
      const replaced = src.replace(regex, (m) => {
        totalReplaced += 1
        return replaceStr
      })
      if (replaced !== src) {
        origItem.text = replaced
      }
    })
  }

  if (totalReplaced > 0) {
    hasEditedSubtitle.value = true
    message.success(`替换成功，共替换了 ${totalReplaced} 处内容`)
  } else {
    message.warning('未匹配到相关内容')
  }
}

// 执行重新配音
function executeReDubbing() {
  if (!eqDetail.value.task_id || !selectedEpisodeNumber.value) {
    message.warning('缺少必要参数，无法重新配音')
    return
  }
  
  isLoading.value = true
  
  const dubbingParams = {
    task_id: eqDetail.value.task_id,
    eq_number: selectedEpisodeNumber.value,
    input_subtitle: originalSubtitles.value,
    output_subtitle: translatedSubtitles.value
  }
  
  taskUpdateSubtitles(dubbingParams).then(dubbingRes => {
    const { code, message: msg } = dubbingRes
    if (code === 0) {
      message.success('已提交重新配音任务')
      hasEditedSubtitle.value = false
      emit('success')
      onClose()
    } else {
      message.error(msg || '提交配音任务失败')
    }
  }).catch(err => {
    console.error('重新配音异常:', err)
    message.error(err.message || '重新配音异常')
  }).finally(() => {
    isLoading.value = false
  })
}

// 重新配音
function handleReDubbing() {
  if (!hasEditedSubtitle.value) {
    Modal.confirm({
      title: '提示',
      content: '没有任何编辑，确定要重新配音吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        executeReDubbing()
      }
    })
    return
  }
  executeReDubbing()
}

// 暴露方法给父组件
defineExpose({
  showEdit
})
</script>

<style scoped>
/* 淡入淡出动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.video-edit-overlay {
  position: fixed;
  z-index: 1000;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  flex-direction: column;
}

/* 顶部工具栏 */
.toolbar {
  background-color: #1E1E1E;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.toolbar-content {
  display: flex;
  align-items: center;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-spacer {
  flex: 1;
}

.episode-select {
  min-width: 160px;
}

.header-flex {
  display: flex;
  align-items: center;
  width: 100%;
}

.header-flex-spacer {
  flex: 1;
}

.header-subtitle-toggle {
  color: rgba(255, 255, 255, 0.6);
  padding: 2px 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.header-subtitle-toggle:hover {
  color: #1890ff;
}

.header-subtitle-toggle.subtitle-off {
  color: rgba(255, 255, 255, 0.25);
}

/* .episode-next-btn 使用 antd 默认样式，无需额外覆盖 */

.toolbar-content h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #D9D9D9;
}

/* 底部操作视图 */
.content-area {
  flex: 1;
  display: flex;
  gap: 10px;
  padding: 10px;
  overflow: hidden;
}

/* 左侧视频区域 - 1份 */
.video-section {
  flex: 1;
  background-color: #1A1A1A;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
}

/* 视频顶部源选择工具条（悬浮于视频左上角） */
.video-source-toolbar {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

.video-source-toolbar .source-left-btn,
.video-source-toolbar .source-right-btn {
  height: 28px;
  line-height: 28px;
  padding: 0 10px;
}

/* 视频播放器 */
.video-section .video-player {
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  display: block;
  background: #000;
  object-fit: contain;
  cursor: pointer;
}

/* 字幕覆盖层 */
.video-section .subtitle-overlay {
  position: absolute;
  width: auto;
  padding: 8px 16px;
  color: #fff;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  border-radius: 4px;
  word-wrap: break-word;
  word-break: break-all;
  white-space: pre-wrap;
  pointer-events: none;
  z-index: 10;
  box-sizing: border-box;
  /* 底部对齐，内容向上撑开 */
  transform-origin: center bottom;
}

/* 翻译字幕覆盖层 - 黑色透明背景 */
.video-section .subtitle-overlay-translated {
  background: rgba(0, 0, 0, 0.7);
  z-index: 11; /* 翻译字幕在原版字幕上方 */
}

/* 原版字幕覆盖层 - 黑色透明背景 */
.video-section .subtitle-overlay-original {
  background: rgba(0, 0, 0, 0.7);
  z-index: 10; /* 原版字幕在底部 */
}

/* 底部视频工具栏 */
.video-section .video-toolbar {
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 进度条区域 */
.video-section .video-progress {
  width: 100%;
}

/* 时间显示 */
.video-section .time-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #D9D9D9;
}

.video-section .current-time,
.video-section .duration-time {
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

/* 进度条包装器 */
.video-section .progress-bar-wrapper {
  width: 100%;
  height: 4px;
  position: relative;
  cursor: pointer;
}

.video-section .progress-bar-bg {
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.video-section .progress-bar-fill {
  height: 100%;
  background-color: #1890ff;
  border-radius: 2px;
  transition: width 0.1s linear;
  position: absolute;
  left: 0;
  top: 0;
}

/* 功能按钮条 */
.video-section .video-controls {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

/* 中间控制按钮组 */
.video-section .control-center {
  display: flex;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

/* 控制按钮 */
.video-section .control-btn {
  color: #D9D9D9;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 播放速度按钮 - 字体小一点，固定宽度 */
.video-section .playback-rate-btn {
  font-size: 12px;
  min-width: 40px;
  width: 40px;
  text-align: center;
}

/* 图标按钮 - 图标大一点 */
.video-section .icon-btn {
  font-size: 18px;
}

.video-section .control-placeholder {
  min-width: 40px;
  width: 40px;
  visibility: hidden;
}

.video-section .control-btn:hover {
  color: #1890ff;
  background-color: rgba(255, 255, 255, 0.1);
}

/* 字幕容器（包含原字幕和翻译字幕）- 2份 */
.subtitle-container {
  flex: 2;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 4px;
  min-height: calc(100vh - 64px - 20px); /* 视口高度 - 工具栏高度 - padding */
  background-color: #0F0F0F;
}

/* 翻译字幕区域（含原字幕对照列） */
.translated-subtitle-section {
  width: 100%;
  background-color: #1E1E1E;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px - 20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* 字幕列表容器 */
.subtitle-list {
  padding: 0;
  width: 100%;
  min-height: 100%;
  overflow: visible;
}

/* 表头 */
.subtitle-header {
  display: flex;
  background-color: #2A2A2A;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
}

.subtitle-header-cell {
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}



.subtitle-header-cell:last-child {
  border-right: none;
}

/* 4列布局宽度约束（时间轴 | 原字幕 | 翻译字幕 | 语速） */
.subtitle-time {
  flex: 0 0 150px !important;
}

.subtitle-original-text {
  flex: 1 1 0 !important;
  min-width: 0;
}

.subtitle-text {
  flex: 1 1 0 !important;
  min-width: 0;
}

.subtitle-speed {
  flex: 0 0 120px !important;
}

/* 表头同样约束 */
.subtitle-header-cell.header-col-time {
  flex: 0 0 150px;
}

.subtitle-header-cell.header-col-original {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.subtitle-header-cell.header-col-translated {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.subtitle-header-cell.header-col-speed {
  flex: 0 0 120px;
}

/* 字幕项分组容器 */
.subtitle-item-group {
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.subtitle-item-group:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.subtitle-item-group-active {
  background-color: rgba(64, 169, 255, 0.15) !important;
}

.subtitle-item-group-active:hover {
  background-color: rgba(64, 169, 255, 0.2) !important;
}

/* 工具栏行 */
.subtitle-item-toolbar {
  display: flex;
  height: 36px;
  background-color: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.toolbar-cell {
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}

.toolbar-cell:last-child {
  border-right: none;
}

.toolbar-col-time {
  flex: 0 0 150px;
  display: flex;
  align-items: center;
}

.subtitle-index {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.toolbar-col-original {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toolbar-col-translated {
  flex: 1 1 0;
  min-width: 0;
}

.toolbar-col-speed {
  flex: 0 0 120px;
}

.speaker-select {
  width: 50%;
}

.speaker-select :deep(.ant-select-selector) {
  background: transparent !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: #D9D9D9 !important;
  height: 26px !important;
  min-height: 26px !important;
}

.speaker-select.ant-select-disabled :deep(.ant-select-selector) {
  background: transparent !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
  cursor: default !important;
}

.speaker-select :deep(.ant-select-selection-item),
.speaker-select :deep(.ant-select-selection-placeholder) {
  line-height: 24px !important;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.speaker-select :deep(.ant-select-arrow) {
  color: rgba(255, 255, 255, 0.4);
}

.translate-btn {
  margin-left: 8px;
  font-size: 12px;
  color: #1890ff;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}
.translate-btn:hover {
  color: #40a9ff;
}
.translate-btn-loading {
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
}

/* 翻译字幕项 */
.subtitle-item-translated {
  display: flex;
}

/* 字幕项 */
/* 最后一行占位 */
.subtitle-placeholder {
  height: 90px;
  width: 100%;
  flex-shrink: 0;
}

.subtitle-item {
  display: flex;
  height: 90px;
  position: relative;
  cursor: pointer;
}


/* 合并/添加按钮容器（行底部居中） */
.subtitle-divider-buttons {
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.subtitle-item:hover .subtitle-divider-buttons {
  opacity: 1;
  pointer-events: auto;
}

/* 删除按钮容器（行尾部） */
.subtitle-delete-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.subtitle-item:hover .subtitle-delete-btn {
  opacity: 1;
  pointer-events: auto;
}

/* 操作按钮通用样式 */
.divider-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2A2A2A;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
}

.divider-btn:hover {
  background-color: #3A3A3A;
  border-color: rgba(255, 255, 255, 0.4);
  color: #FFFFFF;
}

.divider-btn.delete-btn:hover {
  background-color: #3A2020;
  border-color: rgba(255, 68, 68, 0.5);
  color: #FF4444;
}

.divider-btn .anticon {
  font-size: 14px;
}

/* 字幕单元格 */
.subtitle-cell {
  padding: 10px 12px;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.subtitle-cell:last-child {
  border-right: none;
}

/* 字幕时间 */
.subtitle-time {
  font-size: 14px;
  color: #D9D9D9;
  font-weight: 500;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: 10px;
}

.time-start,
.time-end {
  line-height: 1.4;
  white-space: nowrap;
  font-size: 14px;
  color: #D9D9D9;
}

.time-start {
  margin-bottom: 4px;
}

/* 原字幕对照列 */
.subtitle-original-text {
  font-size: 14px;
  overflow: hidden;
  position: relative;
}

.subtitle-textarea-original {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* 字幕文本 */
.subtitle-text {
  font-size: 14px;
  color: #D9D9D9;
  overflow: hidden;
  position: relative;
}

.subtitle-text-content {
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  line-height: 1.6;
  word-break: break-word;
  padding-right: 4px;
  /* 限制显示3行的高度 */
  max-height: calc(1.6em * 3);
}

/* 翻译字幕区域 - 可编辑输入框样式 */
.translated-subtitle-section .subtitle-time {
  gap: 0;
}

.translated-subtitle-section .time-input {
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  color: #D9D9D9;
  line-height: 1.4;
  white-space: nowrap;
  font-family: inherit;
  display: block;
}

.translated-subtitle-section .time-start-input {
  margin-bottom: 4px;
}

.translated-subtitle-section .time-end-input {
  font-size: 14px;
  color: #D9D9D9;
}

/* 输入框错误样式 - 红色字体 */
.translated-subtitle-section .time-input.input-error {
  color: #FF4444 !important;
}

/* 文本红色样式 */
.text-red {
  color: #FF4444 !important;
}

.translated-subtitle-section .subtitle-textarea {
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  color: #D9D9D9;
  line-height: 1.6;
  word-break: break-word;
  resize: none;
  overflow-y: auto;
  overflow-x: hidden;
  font-family: inherit;
  /* 限制显示3行的高度 */
  max-height: calc(1.6em * 3);
}

/* 自定义滚动条样式 */
.translated-subtitle-section .subtitle-textarea::-webkit-scrollbar {
  width: 4px;
}

.translated-subtitle-section .subtitle-textarea::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.translated-subtitle-section .subtitle-textarea::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.translated-subtitle-section .subtitle-textarea::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 自定义滚动条样式 */
.subtitle-text-content::-webkit-scrollbar {
  width: 4px;
}

.subtitle-text-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.subtitle-text-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.subtitle-text-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 语速 */
.subtitle-speed {
  font-size: 14px;
  color: #D9D9D9;
  white-space: nowrap;
  position: relative;
}

</style>
